import { useState, useEffect, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { Button, Input, FormField, Alert, Spinner, Heading } from "@/components/ds";
import { useSignupWizard } from "./SignupWizardProvider";
import styles from "./SignupNameStep.module.css";

export function SignupNameStep() {
  const navigate = useNavigate();
  const { email, password, name, setName } = useSignupWizard();
  const [localName, setLocalName] = useState(name);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email || !password) {
      navigate("/signup", { replace: true });
    }
  }, [email, password, navigate]);

  if (!email || !password) return null;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = localName.trim();

    if (!trimmed) {
      setError("Please enter your name");
      return;
    }

    setName(trimmed);
    setError("");
    setMessage("");
    setLoading(true);

    try {
      const response = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name: trimmed }),
      });

      const data = await response.json();

      if (!response.ok && !data.success) {
        setError(data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      if (data.data?.message) {
        setMessage(data.data.message);
        return;
      }

      if (data.data?.redirectTo) {
        navigate(data.data.redirectTo);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <Heading level={1} size="h3">What is your name?</Heading>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="info">{message}</Alert>}

      <FormField label="Your name" htmlFor="signup-name">
        <Input
          type="text"
          name="name"
          id="signup-name"
          value={localName}
          onChange={(e) => {
            setLocalName(e.target.value);
            setError("");
          }}
          disabled={loading || !!message}
          required
        />
      </FormField>

      {!message && (
        <Button type="submit" disabled={loading} fullWidth>
          {loading ? <Spinner size="sm" /> : "Create account"}
        </Button>
      )}
    </form>
  );
}
